import {
  FileUpdateContextThumbnailModal,
  UserEditProfileModal,
  UserAddConnectionsModal,
  UserSendInviteModal,
  UserDetailsModal,
  UserAccountManagementModal
} from '#components';
import {html2text} from '~/utils/sanitizers.js';

export function useUserActions() {

  async function addConnection({user}) {
    try {
      await useUsersStore().addConnection({user});
    } catch (e) {
      useErrorToast().add();
    }
  }

  async function addConnections() {
    return new Promise(resolve => {
      useFModal({
        id: 'add-connections',
        component: UserAddConnectionsModal,
        size: 'sm-md',
        clickToClose: false,
        eventHandlers: {
          async complete() {
            resolve();
          }
        }
      }).open();
    });
  }

  async function sendInvites() {
    return new Promise(resolve => {
      useFModal({
        id: 'send-invites',
        component: UserSendInviteModal,
        size: 'sm-md',
        clickToClose: false,
        eventHandlers: {
          async complete() {
            resolve();
          }
        }
      }).open();
    });
  }

  async function removeConnection({user}) {
    return new Promise(resolve => {
      const userName = html2text(user.name);
      useConfirmModal({
        heading: `Remove Connection?`,
        message: `Are you sure you want to remove "${userName}" from your Connections? They'll lose access to all content you've shared.`,
        confirmMethod: async () => {
          try {
            await useUsersStore().removeConnection({user});
          } catch (e) {
            useErrorToast().add();
          }
        },
        onConfirm: () => resolve()
      }).open()
    });
  }

  async function editProfile({user}) {
    useFModal({
      id: 'edit-user',
      clickToClose: false,
      component: UserEditProfileModal,
      props: {user}
    }).open();
  }

  function editThumbnail({user}) {
    const modal = useFModal({
      id: 'edit-profile-photo',
      component: FileUpdateContextThumbnailModal,
      size: 'fullscreen',
      showClose: false,
      props: {
        name: user.name
      },
      eventHandlers: {
        async complete(file) {
          try {
            await useFileActions().setFileAsProfilePhoto({file});
            modal.close();
          } catch (e) {
            useErrorToast().add();
          }
        }
      }
    });

    modal.open();
  }

  function editBanner({user}) {
    const modal = useFModal({
      id: 'edit-profile-banner',
      component: FileUpdateContextThumbnailModal,
      size: 'fullscreen',
      showClose: false,
      props: {
        title: 'Select a profile banner',
        userId: String(user.id),
        contextType: FILE_CONTEXTS.gallery,
        scopeFileTypes: [FILE_TYPES.photo]
      },
      eventHandlers: {
        async complete(file) {
          try {
            await useSessionStore().updateCurrentUser({
              banner_file_id: file.id
            });
            useSuccessToast().add({
              id: 'profile-banner-updated',
              title: 'Profile Banner Updated',
              description: 'Your profile banner has been updated.'
            });
            modal.close();
          } catch (e) {
            useErrorToast().add();
          }
        }
      }
    });

    modal.open();
  }

  function openDetailsModal({user}) {
    useFModal({
      id: 'user-details',
      component: UserDetailsModal,
      size: 'lg',
      closeButtonProps: {
        color: 'white',
        variant: 'ghost',
        class: 'rounded-full bg-black/50 text-white hover:bg-black/70 focus-visible:ring-white'
      },
      props: {user}
    }).open();
  }

  function openAccountManagementModal() {
    useFModal({
      id: 'account-management',
      component: UserAccountManagementModal,
    }).open();
  }

  return {
    addConnection,
    addConnections,
    sendInvites,
    removeConnection,
    editProfile,
    editThumbnail,
    editBanner,
    openDetailsModal,
    openAccountManagementModal
  };
}
